import {DateTime} from 'luxon';
import {TEAM_ROLE_ORDER,teamRoleLabel} from '../../utils/teamRoleDisplay';
import {eventTagLabel,eventSlots} from '../../utils/eventSetup';

export function agendaDay(date,zone) {
  return DateTime.fromObject({year:date.getFullYear(),month:date.getMonth()+1,day:date.getDate()},{zone}).startOf('day');
}

export function eventsForAgendaDay(events,date,zone) {
  const day=agendaDay(date,zone),start=day.toMillis(),end=day.plus({days:1}).toMillis();
  return events.filter(event=>{
    const from=Date.parse(event.startUTC),to=Date.parse(event.endUTC);
    return Number.isFinite(from) && (Number.isFinite(to) && to>from ? from<end && to>start : from>=start && from<end);
  }).sort((a,b)=>Date.parse(a.startUTC)-Date.parse(b.startUTC) || agendaEventTitle(a).localeCompare(agendaEventTitle(b)));
}

export function agendaScheduledMinutes(events,date,zone) {
  const day=agendaDay(date,zone),start=day.toMillis(),end=day.plus({days:1}).toMillis();
  return events.reduce((sum,event)=>{
    if (isAgendaEventCancelled(event)) return sum;
    const from=Date.parse(event.startUTC),to=Date.parse(event.endUTC);
    return sum+(Number.isFinite(from) && Number.isFinite(to)?Math.max(0,Math.min(to,end)-Math.max(from,start))/60000:0);
  },0);
}

export function agendaDuration(minutes) {
  const rounded=Math.max(0,Math.round(minutes)),hours=Math.floor(rounded/60),rest=rounded%60;
  return hours ? `${hours}h${rest?` ${rest}m`:''}` : `${rest}m`;
}

// Keep occurrences and matching event IDs from different calendars distinct.
export function agendaEventIdentity(event) {
  return JSON.stringify([event.calendar,event.__teamId,event.eventId||event.id,event.instanceDate||event.startUTC,event.startUTC]);
}

export function isAgendaEventCancelled(event) {
  return Boolean(event.cancelled || event.canceled || ['cancelled','canceled'].includes(String(event.status).toLowerCase()));
}

export function agendaEventTitle(event) {
  const explicit=event.title || event.matchTitle || (event.mapSlots?.length===1 && event.mapSlots[0].titleOverride);
  if (explicit) return String(explicit);
  if (event.competition || event.opponentName || event.opponent) return [event.competition,eventTagLabel(event),(event.opponentName || event.opponent) && `vs ${event.opponentName || event.opponent}`].filter(Boolean).join(' · ');
  return String(event.note || eventTagLabel(event) || 'Calendar event');
}

export function agendaEventDetails(event) {
  const maps=[...new Set(eventSlots(event).map(slot=>slot.mapName).filter(name=>name && !/^(unknown|map tbd)$/i.test(name)))];
  return [event.competition,(event.opponentName || event.opponent) && `vs ${event.opponentName || event.opponent}`,maps.join(' / '),/^bo[1357]$/.test(event.matchFormat || '') && `Best of ${event.matchFormat.slice(2)}`].filter(Boolean).join(' · ');
}

export function agendaParticipants(event,roster=[],team={}) {
  const members=[...roster,...(Array.isArray(team?.members)?team.members.filter(member=>member && typeof member==='object'):[]),...Object.entries(team?.roster || {}).filter(([,member])=>member && typeof member==='object').map(([uid,member])=>({uid,...member})),...Object.entries(team?.memberUsernames||{}).filter(([,name])=>typeof name==='string').map(([uid,username])=>({uid,username}))];
  const names=Array.isArray(event.participants)?event.participants:[];
  const uids=Array.isArray(event.participantsUid)?event.participantsUid:[];
  const rows=names.length?names:uids.map(uid=>({uid,username:members.find(member=>member.uid===uid)?.username || 'Team member'}));
  const seen=new Set();
  return rows.map((person,index)=>{
    const name=typeof person==='string'?person:String(person?.username || person?.name || 'Team member');
    const namedMember=members.find(candidate=>String(candidate.username || candidate.name || '').trim().toLowerCase()===name.trim().toLowerCase());
    // Names and UID arrays can be ordered differently after attendance updates.
    const uid=(typeof person==='object'?person?.uid:null) || namedMember?.uid || uids[index];
    const member=members.find(candidate=>uid && candidate.uid===uid) || namedMember;
    const identity=uid || member?.uid;
    const role=team?.teamRoles?.[identity] || member?.role;
    return {name,uid:identity,role:role && teamRoleLabel(typeof role==='object'?role.role:role),status:event.participantStates?.[identity]?.status};
  }).filter(person=>{const key=person.uid || person.name.toLowerCase();if(seen.has(key))return false;seen.add(key);return true;})
    .sort((a,b)=>(a.role?TEAM_ROLE_ORDER.indexOf(a.role):99)-(b.role?TEAM_ROLE_ORDER.indexOf(b.role):99) || a.name.localeCompare(b.name));
}
